(() => {
  const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  let activeDrawer;
  let drawerOpener;

  function drawerTriggers(drawer) {
    return document.querySelectorAll(`[aria-controls="${drawer.id}"]`);
  }

  function isRendered(element) {
    return element.getClientRects().length > 0 && !element.closest('[aria-hidden="true"]');
  }

  function focusableElements(container) {
    return [...container.querySelectorAll(focusableSelector)].filter(isRendered);
  }

  function closeDrawer({ returnFocus = true } = {}) {
    if (!activeDrawer) return;

    const drawer = activeDrawer;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('drawer-open');
    drawerTriggers(drawer).forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
    activeDrawer = undefined;

    if (returnFocus && drawerOpener && document.contains(drawerOpener)) drawerOpener.focus();
    drawerOpener = undefined;
  }

  function openDrawer(drawer, opener) {
    if (!drawer) return;
    if (activeDrawer && activeDrawer !== drawer) closeDrawer({ returnFocus: false });

    activeDrawer = drawer;
    drawerOpener = opener;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('drawer-open');
    drawerTriggers(drawer).forEach((trigger) => trigger.setAttribute('aria-expanded', 'true'));

    const initialFocus = focusableElements(drawer)[0];
    if (initialFocus) initialFocus.focus();
  }

  function setDropdownState(item, isOpen) {
    const toggle = item.querySelector('[data-dropdown-toggle]');
    const dropdown = item.querySelector('.b2b-nav__dropdown');
    item.classList.toggle('is-open', isOpen);
    if (toggle) toggle.setAttribute('aria-expanded', String(isOpen));
    if (dropdown) dropdown.hidden = !isOpen;
  }

  function closeDesktopDropdowns(except) {
    document.querySelectorAll('[data-nav-item].is-open').forEach((item) => {
      if (item === except) return;
      setDropdownState(item, false);
    });
  }

  function initDrawerInteractions() {
    document.addEventListener('click', (event) => {
      const menuToggle = event.target.closest('[data-menu-toggle]');
      if (menuToggle) {
        event.preventDefault();
        openDrawer(document.getElementById(menuToggle.getAttribute('aria-controls')), menuToggle);
        return;
      }

      const drawerOpen = event.target.closest('[data-drawer-open]');
      if (drawerOpen) {
        const drawer = document.querySelector(drawerOpen.dataset.drawerOpen);
        if (drawer) {
          event.preventDefault();
          openDrawer(drawer, drawerOpen);
        }
        return;
      }

      if (event.target.closest('[data-drawer-close]')) {
        closeDrawer();
        return;
      }

      if (event.target.closest('[data-drawer-navigation]')) closeDrawer({ returnFocus: false });

      const dropdownToggle = event.target.closest('[data-dropdown-toggle]');
      if (dropdownToggle) {
        const item = dropdownToggle.closest('[data-nav-item]');
        const dropdown = item.querySelector('.b2b-nav__dropdown');
        const isOpen = dropdown.hidden;
        closeDesktopDropdowns(item);
        setDropdownState(item, isOpen);
        return;
      }

      if (!event.target.closest('[data-nav-item]')) closeDesktopDropdowns();
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        if (activeDrawer) {
          closeDrawer();
          return;
        }
        const openToggle = document.querySelector('[data-dropdown-toggle][aria-expanded="true"]');
        closeDesktopDropdowns();
        if (openToggle) openToggle.focus();
        return;
      }

      if (event.key !== 'Tab' || !activeDrawer) return;
      const focusable = focusableElements(activeDrawer);
      if (!focusable.length) {
        event.preventDefault();
        activeDrawer.querySelector('.b2b-drawer__panel')?.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!activeDrawer.contains(document.activeElement) || (event.shiftKey && document.activeElement === first)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    document.querySelectorAll('[data-nav-item]').forEach((item) => {
      item.addEventListener('pointerenter', () => {
        closeDesktopDropdowns(item);
        setDropdownState(item, true);
      });
      item.addEventListener('pointerleave', () => setDropdownState(item, false));
      item.addEventListener('focusin', () => {
        closeDesktopDropdowns(item);
        setDropdownState(item, true);
      });
      item.addEventListener('focusout', (event) => {
        if (!item.contains(event.relatedTarget)) setDropdownState(item, false);
      });
    });
  }

  function initCarousel(carousel) {
    if (carousel.dataset.carouselInitialized === 'true') return;
    const slides = [...carousel.querySelectorAll('[data-slide]')];
    if (slides.length < 2) return;
    carousel.dataset.carouselInitialized = 'true';

    let index = slides.findIndex((slide) => slide.classList.contains('is-active'));
    let timer;
    let paused = false;
    const speed = Number(carousel.dataset.speed) || 10000;
    const pauseButton = carousel.querySelector('[data-pause]');
    const canAutoplay = carousel.dataset.autoplay === 'true' && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    function show(nextIndex) {
      index = (nextIndex + slides.length) % slides.length;
      slides.forEach((slide, slideIndex) => {
        const active = slideIndex === index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
      });
    }

    carousel.b2bShowSlide = show;

    function stop() {
      window.clearInterval(timer);
      timer = undefined;
    }

    function start() {
      stop();
      if (canAutoplay && !paused && !carousel.contains(document.activeElement)) {
        timer = window.setInterval(() => show(index + 1), speed);
      }
    }

    function updatePauseButton() {
      const action = paused ? 'Play' : 'Pause';
      pauseButton?.setAttribute('aria-pressed', String(paused));
      pauseButton?.setAttribute('aria-label', `${action} slideshow`);
      if (pauseButton) {
        pauseButton.querySelector('[data-pause-icon]')?.toggleAttribute('hidden', paused);
        pauseButton.querySelector('[data-play-icon]')?.toggleAttribute('hidden', !paused);
      }
    }

    carousel.querySelector('[data-prev]')?.addEventListener('click', () => {
      show(index - 1);
      start();
    });
    carousel.querySelector('[data-next]')?.addEventListener('click', () => {
      show(index + 1);
      start();
    });
    pauseButton?.addEventListener('click', () => {
      paused = !paused;
      updatePauseButton();
      start();
    });
    carousel.addEventListener('focusin', stop);
    carousel.addEventListener('focusout', (event) => {
      if (!carousel.contains(event.relatedTarget)) start();
    });
    updatePauseButton();
    start();
    carousel.b2bCarouselDestroy = () => {
      stop();
      delete carousel.b2bShowSlide;
      delete carousel.b2bCarouselDestroy;
      delete carousel.dataset.carouselInitialized;
    };
  }

  initDrawerInteractions();
  document.querySelectorAll('[data-carousel]').forEach(initCarousel);
  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll?.('[data-carousel]').forEach(initCarousel);
  });
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll?.('[data-carousel]').forEach((carousel) => carousel.b2bCarouselDestroy?.());
  });
  document.addEventListener('shopify:block:select', (event) => {
    const slide = event.target.closest?.('[data-slide]');
    if (!slide) return;
    const carousel = slide.closest('[data-carousel]');
    const slides = carousel ? [...carousel.querySelectorAll('[data-slide]')] : [];
    const index = slides.indexOf(slide);
    if (carousel && index >= 0) carousel.b2bShowSlide?.(index);
  });
  document.querySelector('[data-form-success]')?.focus();
})();
